import React from 'react';
import { Check, Crown, Ticket, Sparkles, ExternalLink, Plus, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { eventInfo } from '../data/mock';

// Pricing windows. Dates are HST event-local; the Date constructor uses local
// browser time which is fine for a coarse "which tier is active right now" hint.
const PRICING_WINDOWS = [
  {
    id: 'early-bird',
    label: 'Early Bird',
    sub: 'Ends May 29',
    ends: new Date('2026-05-30T00:00:00-10:00'),
    ga: 55,
    vip: 85
  },
  {
    id: 'presale',
    label: 'Presale',
    sub: 'May 30 – Jun 7',
    ends: new Date('2026-06-08T00:00:00-10:00'),
    ga: 65,
    vip: 110
  },
  {
    id: 'regular',
    label: 'Regular',
    sub: 'Jun 8 – Jun 20',
    ends: new Date('2026-06-21T00:00:00-10:00'),
    ga: 75,
    vip: 125
  }
];

const getActiveWindow = () => {
  const now = new Date();
  return PRICING_WINDOWS.find((w) => now < w.ends) || PRICING_WINDOWS[PRICING_WINDOWS.length - 1];
};

const BASE_PERKS = [
  'Festival entry · 5 PM – 9 PM',
  'Tasting glass + 21+ wristband',
  '80+ tequila, mezcal & agave brand pours',
  '3 tasting plates from participating restaurants',
  'Live music, DJs, mariachi & live painting',
  'Artisan flea market access',
  'Street food access from top Oʻahu chefs'
];

const VIP_EXTRAS = [
  'Early entry 4–5 PM — VIP doors open 1 hour before GA',
  'Private VIP entrance — skip the line',
  'Additive-free tequila table',
  'Añejo & Extra Añejo pours',
  'Rare tequila vault & limited-pour bottles',
  'Private VIP lounge & cabana seating'
];

const PriceLadder = ({ activeId, kind }) => (
  <div className="space-y-1.5" data-testid={`price-ladder-${kind}`}>
    {PRICING_WINDOWS.map((w) => {
      const isActive = w.id === activeId;
      const price = kind === 'ga' ? w.ga : w.vip;
      return (
        <div
          key={w.id}
          data-testid={`price-row-${kind}-${w.id}`}
          className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg transition-colors ${
            isActive
              ? 'bg-white/10 border border-white/20'
              : 'bg-transparent border border-transparent opacity-55'
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            {isActive && (
              <span className="bg-gradient-to-r from-pink-500 to-orange-500 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full shadow-sm shrink-0">
                Now
              </span>
            )}
            <div className="min-w-0">
              <div className={`text-xs sm:text-sm font-bold leading-tight ${isActive ? 'text-white' : 'text-white/70'}`}>
                {w.label}
              </div>
              <div className="text-[10px] sm:text-xs text-white/50 leading-tight">{w.sub}</div>
            </div>
          </div>
          <div className={`text-lg sm:text-xl font-black tabular-nums shrink-0 ${isActive ? 'text-white' : 'text-white/55'}`}>
            ${price}
          </div>
        </div>
      );
    })}
  </div>
);

const VipComparisonSection = () => {
  const active = getActiveWindow();
  const openTickets = () => window.open(eventInfo.ticketUrl, '_blank', 'noopener,noreferrer');

  return (
    <section
      id="vip-comparison"
      className="py-20 sm:py-24 bg-gradient-to-b from-black via-gray-950 to-black relative overflow-hidden"
      data-testid="vip-comparison-section"
    >
      <div className="absolute top-1/3 -right-32 w-[28rem] h-[28rem] bg-yellow-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/3 -left-32 w-[28rem] h-[28rem] bg-pink-500/10 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/30 mb-4">
            <Sparkles className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-300 text-sm font-medium tracking-wider uppercase">Choose Your Experience</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-orange-400 to-yellow-400 mb-4">
            General vs VIP
          </h2>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
            Two ways to experience Hawaii&apos;s premier tequila festival. Prices step up as we get closer to event day — Early Bird is the best deal.
          </p>
        </div>

        {/* Active-tier urgency banner */}
        <div className="flex items-center justify-center gap-2 mb-8" data-testid="active-window-banner">
          <Clock className="w-4 h-4 text-pink-400" />
          <span className="text-white/85 text-sm sm:text-base">
            <span className="font-bold text-white">{active.label} pricing</span> active now ·{' '}
            <span className="text-white/65">{active.sub}</span>
          </span>
        </div>

        {/* Tier cards */}
        <div className="grid md:grid-cols-2 gap-5 sm:gap-6 mb-10">
          {/* General Admission */}
          <div
            data-testid="tier-card-general"
            className="relative bg-gradient-to-br from-pink-500/15 to-orange-500/5 backdrop-blur-sm border border-white/15 rounded-3xl p-6 sm:p-8 transition-all duration-300 hover:-translate-y-1 flex flex-col"
          >
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bg-white/10 text-pink-300">
                <Ticket className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-2xl sm:text-3xl font-black text-white leading-tight">General Admission</h3>
                <p className="text-white/65 text-sm mt-1">The full festival experience</p>
              </div>
            </div>

            <PriceLadder activeId={active.id} kind="ga" />

            <ul className="space-y-2.5 mt-6 mb-7 flex-grow">
              {BASE_PERKS.map((perk) => (
                <li key={perk} className="flex items-start gap-3 text-sm sm:text-base text-white/85">
                  <Check className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>

            <Button
              onClick={openTickets}
              data-testid="tier-cta-general"
              className="w-full h-12 font-bold rounded-xl inline-flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.02] bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white"
            >
              <Ticket className="w-5 h-5" />
              Get GA — ${active.ga}
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>

          {/* VIP */}
          <div
            data-testid="tier-card-vip"
            className="relative bg-gradient-to-br from-yellow-400/25 via-pink-500/15 to-orange-500/10 backdrop-blur-sm border border-yellow-400/40 rounded-3xl p-6 sm:p-8 transition-all duration-300 hover:-translate-y-1 flex flex-col shadow-2xl shadow-yellow-500/10"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-400 text-black text-xs font-black uppercase tracking-wider px-4 py-1 rounded-full shadow-lg">
              Best Experience
            </div>

            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-yellow-400 to-orange-400 text-black">
                <Crown className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-2xl sm:text-3xl font-black text-white leading-tight">VIP Experience</h3>
                <p className="text-white/65 text-sm mt-1">Limited capacity · Sells out first</p>
              </div>
            </div>

            <PriceLadder activeId={active.id} kind="vip" />

            <ul className="space-y-2.5 mt-6 mb-4">
              {BASE_PERKS.map((perk) => (
                <li key={perk} className="flex items-start gap-3 text-sm sm:text-base text-white/85">
                  <Check className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>

            {/* VIP unlocks divider */}
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-yellow-400/40 to-transparent" />
              <div className="flex items-center gap-1.5 text-yellow-300 text-xs font-bold uppercase tracking-wider">
                <Plus className="w-3.5 h-3.5" /> VIP unlocks
              </div>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-yellow-400/40 to-transparent" />
            </div>

            <ul className="space-y-2.5 mb-7 flex-grow rounded-2xl bg-yellow-400/5 border border-yellow-400/20 p-4">
              {VIP_EXTRAS.map((perk) => (
                <li key={perk} className="flex items-start gap-3 text-sm sm:text-base text-white">
                  <Crown className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-1" />
                  <span className="font-medium">{perk}</span>
                </li>
              ))}
            </ul>

            <Button
              onClick={openTickets}
              data-testid="tier-cta-vip"
              className="w-full h-12 font-bold rounded-xl inline-flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.02] bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-400 hover:from-yellow-500 hover:via-orange-500 hover:to-pink-500 text-black shadow-lg shadow-yellow-500/20"
            >
              <Crown className="w-5 h-5" />
              Get VIP — ${active.vip}
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <p className="text-white/55 text-xs sm:text-sm text-center max-w-3xl mx-auto leading-relaxed">
          Prices listed are per ticket and step up at each window. Final pricing, additional tier options, and quantity availability are confirmed at TicketSpice checkout. <span className="text-pink-300 font-semibold">Add-on: 4 extra restaurant tasting tickets for $10 available at checkout.</span> All tickets non-refundable but transferable to a guest aged 21+. 21+ event · Valid government-issued ID required at entry.
        </p>
      </div>
    </section>
  );
};

export default VipComparisonSection;
